<script setup>
import { ref, computed, onMounted } from 'vue';
import conversationActivitiesAPI from 'dashboard/api/conversationActivities';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null,
  },
  limit: {
    type: Number,
    default: null,
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

// State
const activities = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const currentPage = ref(1);
const totalCount = ref(0);
const totalPages = ref(0);
const hasMore = ref(true);

// Activity types and their configurations
const activityConfig = {
  stage_changed: {
    icon: 'i-lucide-arrow-right',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Estágio alterado',
  },
  note_added: {
    icon: 'i-lucide-sticky-note',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Nota adicionada',
  },
  checklist_updated: {
    icon: 'i-lucide-check-square',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'Checklist atualizado',
  },
  attachment_added: {
    icon: 'i-lucide-paperclip',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: 'Anexo adicionado',
  },
  assignee_changed: {
    icon: 'i-lucide-user',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    label: 'Responsável alterado',
  },
  priority_changed: {
    icon: 'i-lucide-flag',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'Prioridade alterada',
  },
  message_sent: {
    icon: 'i-lucide-message-circle',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    label: 'Mensagem enviada',
  },
  created: {
    icon: 'i-lucide-plus-circle',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: 'Lead criado',
  },
};

// Removido mock data - usando dados reais do backend

const formatDate = dateString => {
  if (!dateString) return 'agora';

  const date = new Date(dateString);
  const now = new Date();

  // Verificar se a data é válida
  if (isNaN(date.getTime())) return 'agora';

  const diffInMinutes = Math.floor((now - date) / (1000 * 60));

  // Evitar valores negativos
  if (diffInMinutes < 0) return 'agora';
  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getActivityConfig = type => {
  return activityConfig[type] || activityConfig.created;
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
};

const getActivityDescription = activity => {
  switch (activity.type) {
    case 'stage_changed':
      return `Moveu de "${activity.metadata.from_stage}" para "${activity.metadata.to_stage}"`;
    case 'note_added':
      return `Adicionou nota: "${activity.metadata.note_content}"`;
    case 'checklist_updated':
      const action =
        activity.metadata.action === 'completed' ? 'concluiu' : 'adicionou';
      return `${action} item "${activity.metadata.item_title}"`;
    case 'attachment_added':
      return `Anexou arquivo "${activity.metadata.file_name}" (${formatFileSize(activity.metadata.file_size)})`;
    case 'assignee_changed':
      if (!activity.metadata.from_assignee) {
        return `Atribuiu para ${activity.metadata.to_assignee}`;
      }
      return `Mudou responsável de ${activity.metadata.from_assignee} para ${activity.metadata.to_assignee}`;
    case 'priority_changed':
      return `Alterou prioridade para ${activity.metadata.priority}`;
    case 'message_sent':
      return `Enviou mensagem: "${activity.metadata.message_preview}"`;
    case 'created':
      return 'Lead criado no sistema';
    default:
      return activity.description;
  }
};

// Group activities by date
const groupedActivities = computed(() => {
  const groups = {};

  // Limit activities if prop is provided
  const limitedActivities = props.limit
    ? activities.value.slice(0, props.limit)
    : activities.value;

  limitedActivities.forEach(activity => {
    const date = new Date(activity.created_at);
    const dateKey = date.toDateString();

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: dateKey,
        displayDate: formatDateGroup(date),
        activities: [],
      };
    }

    groups[dateKey].activities.push(activity);
  });

  return Object.values(groups).sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
});

const hasMoreActivities = computed(() => {
  // Se há um limite definido (usado na aba principal), verificar se há mais atividades que o limite
  if (props.limit) {
    return activities.value.length > props.limit;
  }
  // Se não há limite (aba de atividades), usar a flag hasMore da API
  return hasMore.value && activities.value.length < totalCount.value;
});

const remainingActivities = computed(() => {
  if (props.limit) {
    return Math.max(0, activities.value.length - props.limit);
  }
  return Math.max(0, totalCount.value - activities.value.length);
});

const formatDateGroup = date => {
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  // Evitar valores negativos
  if (diffInDays < 0) return 'Hoje';
  if (diffInDays === 0) return 'Hoje';
  if (diffInDays === 1) return 'Ontem';
  if (diffInDays < 7) return `${diffInDays} dias atrás`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const loadActivities = async () => {
  loading.value = true;
  currentPage.value = 1;

  try {
    let response;

    // Se temos um KanbanCard, usar as atividades específicas do card
    if (props.kanbanCard?.id) {
      // Usar atividades específicas do card no pipeline
      response = await conversationActivitiesAPI.get(props.conversation.id, {
        page: 1,
        per_page: 5,
        pipeline_id: props.pipelineId,
        kanban_card_id: props.kanbanCard.id,
      });
    } else {
      // Usar atividades da conversa no contexto do pipeline
      response = await conversationActivitiesAPI.get(props.conversation.id, {
        page: 1,
        per_page: 5,
        pipeline_id: props.pipelineId,
      });
    }

    // Verificar se a resposta tem a nova estrutura com metadados
    if (response.data.activities && response.data.meta) {
      // Nova estrutura com paginação
      activities.value = response.data.activities;
      totalCount.value = response.data.meta.total_count;
      totalPages.value = response.data.meta.total_pages;
      hasMore.value = response.data.meta.has_next_page;
      currentPage.value = response.data.meta.current_page;
    } else {
      // Estrutura antiga - fallback
      const newActivities = response.data.activities || response.data || [];
      activities.value = newActivities;
      totalCount.value = newActivities.length;
      hasMore.value = newActivities.length >= 5;
    }
  } catch (error) {
    console.error('Error loading activities:', error);
    // Sem fallback - usar array vazio
    activities.value = [];
    totalCount.value = 0;
    hasMore.value = false;
  } finally {
    loading.value = false;
  }
};

const loadMoreActivities = async () => {
  if (loadingMore.value || !hasMore.value) return;

  loadingMore.value = true;

  try {
    const nextPage = currentPage.value + 1;
    let response;

    if (props.kanbanCard?.id) {
      response = await conversationActivitiesAPI.get(props.conversation.id, {
        page: nextPage,
        per_page: 5,
        pipeline_id: props.pipelineId,
        kanban_card_id: props.kanbanCard.id,
      });
    } else {
      response = await conversationActivitiesAPI.get(props.conversation.id, {
        page: nextPage,
        per_page: 5,
        pipeline_id: props.pipelineId,
      });
    }

    // Verificar se a resposta tem a nova estrutura com metadados
    if (response.data.activities && response.data.meta) {
      // Nova estrutura com paginação
      const newActivities = response.data.activities;

      if (newActivities.length > 0) {
        activities.value.push(...newActivities);
        currentPage.value = response.data.meta.current_page;
        hasMore.value = response.data.meta.has_next_page;
        totalCount.value = response.data.meta.total_count;
        totalPages.value = response.data.meta.total_pages;
      } else {
        hasMore.value = false;
      }
    } else {
      // Estrutura antiga - fallback
      const newActivities = response.data.activities || response.data || [];

      if (newActivities.length > 0) {
        activities.value.push(...newActivities);
        currentPage.value = nextPage;
        hasMore.value = newActivities.length >= 5;
      } else {
        hasMore.value = false;
      }
    }
  } catch (error) {
    console.error('Error loading more activities:', error);
    hasMore.value = false;
  } finally {
    loadingMore.value = false;
  }
};

onMounted(() => {
  loadActivities();
});
</script>

<template>
  <div class="space-y-4">
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div
        class="animate-spin rounded-full h-6 w-6 border-b-2 border-n-slate-9"
      />
    </div>

    <div v-else-if="!activities.length" class="text-center py-8">
      <div
        class="w-12 h-12 mx-auto mb-3 bg-n-solid-2 rounded-full flex items-center justify-center"
      >
        <i class="i-lucide-activity size-6 text-n-slate-9" />
      </div>
      <p class="text-sm text-n-slate-11">Nenhuma atividade registrada</p>
    </div>

    <div v-else class="space-y-6">
      <div
        v-for="group in groupedActivities"
        :key="group.date"
        class="space-y-3"
      >
        <!-- Date Header -->
        <div class="flex items-center gap-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ group.displayDate }}
          </h4>
          <div class="flex-1 h-px bg-n-weak" />
        </div>

        <!-- Activities in this date group -->
        <div class="space-y-3">
          <div
            v-for="activity in group.activities"
            :key="activity.id"
            class="flex items-start gap-3"
          >
            <!-- Activity Icon -->
            <div class="flex-shrink-0 mt-1">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center"
                :class="getActivityConfig(activity.type).bgColor"
              >
                <i
                  :class="[
                    getActivityConfig(activity.type).icon,
                    getActivityConfig(activity.type).color,
                  ]"
                  class="size-4"
                />
              </div>
            </div>

            <!-- Activity Content -->
            <div class="flex-1 min-w-0 overflow-hidden">
              <div class="flex items-start justify-between gap-2">
                <div class="flex-1 min-w-0 overflow-hidden">
                  <!-- User and Action -->
                  <p class="text-sm text-n-slate-12 leading-relaxed">
                    <span class="block truncate">{{
                      activity.content || getActivityDescription(activity)
                    }}</span>
                  </p>

                  <!-- Timestamp -->
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{ activity.time_ago || formatDate(activity.created_at) }}
                  </p>
                </div>

                <!-- Activity Type Badge -->
                <span
                  class="flex-shrink-0 text-xs px-2 py-1 rounded-full whitespace-nowrap"
                  :class="getActivityConfig(activity.type).bgColor"
                >
                  {{ getActivityConfig(activity.type).label }}
                </span>
              </div>

              <!-- Additional Details for specific activity types -->
              <div
                v-if="activity.type === 'stage_changed'"
                class="mt-2 p-2 bg-n-solid-1 rounded text-xs overflow-hidden"
              >
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="px-2 py-1 bg-gray-200 rounded text-xs truncate"
                    >{{ activity.metadata?.from_stage || 'Anterior' }}</span>
                  <i
                    class="i-lucide-arrow-right size-3 text-n-slate-9 flex-shrink-0"
                  />
                  <span
                    class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs truncate"
                    >{{ activity.metadata?.to_stage || 'Atual' }}</span>
                </div>
              </div>

              <div
                v-if="activity.type === 'note_added'"
                class="mt-2 p-2 bg-n-solid-1 rounded text-xs text-n-slate-11 italic overflow-hidden"
              >
                <span class="block truncate">"{{
                    activity.metadata?.note_content || 'Nota adicionada'
                  }}"</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More Button (for pagination) -->
    <div v-if="hasMoreActivities" class="text-center pt-4">
      <button
        class="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2 mx-auto"
        :disabled="loadingMore"
        @click="loadMoreActivities"
      >
        <i v-if="loadingMore" class="i-lucide-loader-2 size-3 animate-spin" />
        <span v-if="loadingMore">Carregando...</span>
        <span v-else-if="props.limit">
          Carregar mais atividades ({{ remainingActivities }} restantes)
        </span>
        <span v-else>
          Carregar mais atividades ({{ remainingActivities }} restantes)
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Timeline line effect */
.space-y-3 > div:not(:last-child) .flex-shrink-0::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 2rem;
  width: 2px;
  height: 1rem;
  background: #e5e7eb;
  transform: translateX(-50%);
}

/* Prevent horizontal overflow */
.overflow-hidden {
  overflow: hidden;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.min-w-0 {
  min-width: 0;
}

/* Ensure activity content doesn't overflow */
.flex-1 {
  flex: 1 1 0%;
  min-width: 0;
}

/* Activity badges should not wrap */
.whitespace-nowrap {
  white-space: nowrap;
}

/* Ensure proper text wrapping */
.leading-relaxed {
  line-height: 1.625;
}

/* Prevent content from breaking layout */
.space-y-4 > * {
  max-width: 100%;
  overflow-wrap: break-word;
}
</style>
